import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../../lib/supabase'
import type { RawActivityTransaction } from '../../shared/mapTransactionToActivity'

export interface AvailableReward {
  id: string
  name: string
  count: number
}

export interface MemberStampData {
  currentStamps: number
  availableRewards: AvailableReward[]
  recentActivity: RawActivityTransaction[]
}

interface RawReward {
  reward_item_id: string | null
  reward_items: { name: unknown } | { name: unknown }[] | null
}

interface RpcActivityRow {
  id: string
  type: string
  stamps_delta: number
  rewards_delta: number
  created_at: string
  actor_name: string | null
  actor_role: string | null
  correction_reason: string | null
  reward_item_name: Record<string, string> | null
  loyalty_config_snapshot: { earning_method?: string; amount_per_stamp?: number } | null
}

interface RawStampData {
  currentStamps: number
  rewards: RawReward[]
  activityRows: RpcActivityRow[]
}


async function fetchMemberStampData(memberId: string): Promise<RawStampData> {
  const [cardResult, rewardsResult, activityResult] = await Promise.all([
    supabase
      .from('stamp_cards')
      .select('current_stamps')
      .eq('member_id', memberId)
      .single(),
    supabase
      .from('rewards')
      .select('reward_item_id, reward_items ( name )')
      .eq('member_id', memberId)
      .eq('status', 'active'),
    supabase.rpc('get_recent_activity', {
      p_member_id: memberId,
      p_page_size: 5,
    }),
  ])

  if (cardResult.error) throw cardResult.error
  if (rewardsResult.error) throw rewardsResult.error
  if (activityResult.error) throw activityResult.error

  const activityRows = ((activityResult.data as { entries: RpcActivityRow[] } | null)?.entries ?? [])

  return {
    currentStamps: cardResult.data.current_stamps,
    rewards: (rewardsResult.data ?? []) as RawReward[],
    activityRows,
  }
}

export function useMemberStampData(memberId: string | undefined) {
  const { i18n } = useTranslation()
  const lang = i18n.language === 'sv' ? 'sv' : 'en'

  return useQuery<RawStampData, Error, MemberStampData>({
    queryKey: ['member-stamp-data', memberId],
    queryFn: () => fetchMemberStampData(memberId!),
    enabled: !!memberId,
    staleTime: 30 * 1000,
    select: (data): MemberStampData => {
      const rewardCounts = new Map<string, { name: string; count: number }>()

      for (const reward of data.rewards) {
        if (!reward.reward_item_id) continue

        const existing = rewardCounts.get(reward.reward_item_id)
        if (existing) {
          existing.count += 1
        } else {
          const rewardItem = Array.isArray(reward.reward_items) ? reward.reward_items[0] : reward.reward_items
          const nameObj = rewardItem!.name as Record<string, string>
          rewardCounts.set(reward.reward_item_id, { name: nameObj[lang] || nameObj.en, count: 1 })
        }
      }

      const availableRewards: AvailableReward[] = Array.from(rewardCounts.entries()).map(
        ([id, { name, count }]) => ({ id, name, count }),
      )

      const recentActivity: RawActivityTransaction[] = data.activityRows.map((row) => {
        const snapshot = row.loyalty_config_snapshot
        const nameObj = row.reward_item_name
        return {
          id: row.id,
          type: row.type,
          stamps_delta: row.stamps_delta,
          rewards_delta: row.rewards_delta,
          created_at: row.created_at,
          cashier_name: row.actor_name ?? null,
          actor_role: row.actor_role ?? null,
          correction_reason: row.correction_reason,
          reward_item_name: nameObj ? (nameObj[lang] ?? nameObj.en ?? undefined) : undefined,
          earning_method: snapshot?.earning_method ?? null,
          amount_per_stamp: snapshot?.amount_per_stamp ?? null,
        }
      })

      return { currentStamps: data.currentStamps, availableRewards, recentActivity }
    },
  })
}